/**
 * TypeScript-style type definitions for Orders (JSDoc format)
 * These types align with the Orders API Frontend Integration Guide
 */

/**
 * Full Order object
 * @typedef {Object} Order
 * @property {string} orderId - Order ID in format YYMM#### (e.g., "25120001")
 * @property {string} orderDate - ISO date string (YYYY-MM-DD)
 * @property {number} clientId - Client ID
 * @property {string} [clientName] - Client name (from join)
 * @property {string} [clientCode] - Client code (from join)
 * @property {string} [processedDate] - ISO date when samples were processed
 * @property {string} [completedDate] - ISO date when order was completed
 * @property {boolean} [processed] - Whether order is processed (default: false)
 * @property {boolean} [completed] - Whether order is completed (default: false)
 * @property {boolean} [reviewFlag] - Whether order needs review (default: false)
 * @property {string} [createdBy] - Username of user who created the order
 * @property {string} [lastModifiedBy] - Username of user who last modified
 * @property {string} createdAt - ISO datetime string
 * @property {string} lastMod - ISO datetime string
 */

/**
 * Create order request payload
 * @typedef {Object} CreateOrderRequest
 * @property {string} orderDate - Required: ISO date (YYYY-MM-DD)
 * @property {number} clientId - Required: Client ID
 * @property {string} [processedDate] - Optional: Date samples were processed
 * @property {string} [completedDate] - Optional: Date order was completed
 * @property {boolean} [processed] - Optional: Whether order is processed (default: false)
 * @property {boolean} [completed] - Optional: Whether order is completed (default: false)
 * @property {boolean} [reviewFlag] - Optional: Whether order needs review (default: false)
 */

/**
 * Update order request payload
 * @typedef {Object} UpdateOrderRequest
 * @property {string} [orderDate] - ISO date
 * @property {number} [clientId] - Change client
 * @property {string} [processedDate] - Date samples were processed
 * @property {string} [completedDate] - Date order was completed
 * @property {boolean} [processed] - Whether order is processed
 * @property {boolean} [completed] - Whether order is completed
 * @property {boolean} [reviewFlag] - Whether order needs review
 */

/**
 * Count response
 * @typedef {Object} CountResponse
 * @property {number} [clientId] - Client ID (for client count)
 * @property {number} orderCount - Count of orders
 */

/**
 * Paginated response wrapper
 * @typedef {Object} Page
 * @template T
 * @property {T[]} content - Array of items for current page
 * @property {number} totalElements - Total items across all pages
 * @property {number} totalPages - Total number of pages
 * @property {number} size - Page size
 * @property {number} number - Current page (0-indexed)
 * @property {boolean} first - Is first page
 * @property {boolean} last - Is last page
 * @property {boolean} empty - Is page empty
 */

/**
 * Pagination parameters
 * @typedef {Object} PageParams
 * @property {number} [page] - Page number (0-indexed, default: 0)
 * @property {number} [size] - Items per page (default: 20)
 * @property {string} [sort] - Sort field and direction (e.g., "orderDate,desc")
 */

/**
 * Derived order status (computed from dates)
 * @typedef {'Pending' | 'InProgress' | 'Completed'} DerivedOrderStatus
 */

/**
 * Get derived status from order dates
 * @param {Order} order - Order object
 * @returns {DerivedOrderStatus} Derived status
 */
export const getOrderStatus = (order) => {
    if (order.completedDate) return 'Completed';
    if (order.processedDate) return 'InProgress';
    return 'Pending';
};

/**
 * Order status badge color mapping (for derived statuses)
 * @type {Record<DerivedOrderStatus, string>}
 */
export const ORDER_STATUS_COLORS = {
    'Pending': 'yellow',      // No processed date
    'InProgress': 'blue',     // Has processed date, no completed date
    'Completed': 'green'      // Has completed date
};

/**
 * Order status display labels (for derived statuses)
 * @type {Record<DerivedOrderStatus, string>}
 */
export const ORDER_STATUS_LABELS = {
    'Pending': 'Pending',
    'InProgress': 'In Progress',
    'Completed': 'Completed'
};

/**
 * Pagination helper utilities
 */
export const PaginationHelper = {
    /**
     * Build pagination parameters
     * @param {number} page - Page number (0-indexed)
     * @param {number} size - Items per page
     * @param {string} sortField - Field to sort by
     * @param {string} sortDirection - Sort direction (asc/desc)
     * @returns {PageParams} Pagination parameters
     */
    buildPageParams: (page = 0, size = 20, sortField = 'orderDate', sortDirection = 'desc') => ({
        page,
        size,
        sort: `${sortField},${sortDirection}`
    }),
    
    /**
     * Get default sort string
     * @returns {string} Default sort string
     */
    getDefaultSort: () => 'orderDate,desc',
    
    /**
     * Build sort string from field and direction
     * @param {string} field - Field to sort by
     * @param {string} direction - Sort direction (asc/desc)
     * @returns {string} Sort string
     */
    buildSortString: (field, direction) => `${field},${direction}`
};
